import { useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Wallet } from "lucide-react";

const GOLD = "bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest";
const OUT = "border-amber-500/40 text-amber-300 hover:bg-amber-400 hover:text-black";
const label = (r) => r.charAt(0).toUpperCase() + r.slice(1);

export function CreateMemberDialog({ role, onCreated }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", display_name: "", temp_password: "", initial_credits: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/team/members", { ...f, initial_credits: Number(f.initial_credits) || 0 });
      toast.success(`${label(role)} ID created`);
      setOpen(false);
      setF({ email: "", display_name: "", temp_password: "", initial_credits: "" });
      onCreated();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={GOLD} data-testid="create-member-btn">+ New {label(role)}</Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-950 border-amber-500/20">
        <DialogHeader><DialogTitle className="font-display uppercase gold-text">Create {label(role)} ID</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Email (login ID)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-black border-amber-500/20" data-testid="new-member-email" />
          <Input placeholder="Display name" value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} className="bg-black border-amber-500/20" data-testid="new-member-name" />
          <Input placeholder="Temporary password (min 8)" value={f.temp_password} onChange={(e) => setF({ ...f, temp_password: e.target.value })} className="bg-black border-amber-500/20" data-testid="new-member-pw" />
          <Input type="number" min="0" placeholder="Opening balance" value={f.initial_credits} onChange={(e) => setF({ ...f, initial_credits: e.target.value })} className="bg-black border-amber-500/20" data-testid="new-member-credits" />
          <div className="text-[11px] text-zinc-500">
            {user?.role === "admin" ? "Admin issues new virtual credits." : `Opening balance is transferred from your balance (${(user?.credits || 0).toFixed(2)}).`} They must change the password on first login.
          </div>
        </div>
        <DialogFooter>
          <Button disabled={busy} onClick={submit} className={GOLD} data-testid="submit-create-member">{busy ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MemberActions({ m, canLoad, onDone }) {
  const [resetOpen, setResetOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); onDone(); return true; }
    catch (e) { toast.error(formatError(e)); return false; }
  };
  const toggle = () => run(() => api.patch(`/team/members/${m.id}/status`, { disabled: !m.disabled }), m.disabled ? "ID enabled" : "ID disabled");
  const reset = async () => {
    if (await run(() => api.post(`/team/members/${m.id}/reset-password`, { new_temp_password: newPw }), "Password reset")) {
      setResetOpen(false); setNewPw("");
    }
  };
  const move = async (sign) => {
    const delta = sign * Math.abs(Number(amount));
    if (await run(() => api.post(`/team/members/${m.id}/credits`, { delta, reason: reason || (sign > 0 ? "Deposit" : "Withdraw") }), sign > 0 ? "Balance loaded" : "Balance withdrawn")) {
      setCreditOpen(false); setAmount(""); setReason("");
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {canLoad && (
        <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className={OUT} data-testid={`balance-${m.id}`}><Wallet className="h-3 w-3 mr-1" /> D / W</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-amber-500/20">
            <DialogHeader><DialogTitle className="font-display gold-text">Balance · {m.display_name}</DialogTitle></DialogHeader>
            <div className="text-sm text-zinc-400">Current balance <span className="font-mono-stat text-amber-300">{(m.credits || 0).toFixed(2)}</span></div>
            <Input type="number" min="0" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-black border-amber-500/20" data-testid="balance-amount" />
            <Input placeholder="Remark (optional)" value={reason} onChange={(e) => setReason(e.target.value)} className="bg-black border-amber-500/20" data-testid="balance-reason" />
            <div className="text-[11px] text-zinc-500">Deposit moves credits from your balance to theirs; withdraw moves them back. Virtual credits only.</div>
            <DialogFooter className="gap-2">
              <Button onClick={() => move(-1)} variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500 hover:text-white" data-testid="balance-withdraw">Withdraw</Button>
              <Button onClick={() => move(1)} className={GOLD} data-testid="balance-deposit">Deposit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline" className={OUT} data-testid={`reset-${m.id}`}><KeyRound className="h-3 w-3 mr-1" /> Reset</Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-950 border-amber-500/20">
          <DialogHeader><DialogTitle className="font-display gold-text">Reset password · {m.display_name}</DialogTitle></DialogHeader>
          <Input placeholder="New temporary password (min 8)" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-black border-amber-500/20" data-testid="reset-pw-input" />
          <DialogFooter><Button onClick={reset} className={GOLD} data-testid="reset-submit">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Button size="sm" variant="outline" className={OUT} onClick={toggle} data-testid={`toggle-${m.id}`}>{m.disabled ? "Enable" : "Disable"}</Button>
    </div>
  );
}
